import React, { createContext, useContext, useState, useEffect, useCallback, type ReactNode } from 'react';
import { translations, type Language, type TranslationKeys } from '@/i18n/translations';
import { supabase } from '@/integrations/supabase/client';
import { track, AnalyticsEvent } from '@/lib/analytics';


export type ThemeMode = 'dark' | 'light' | 'system';

interface PreferencesContextValue {
  theme: ThemeMode;
  setTheme: (t: ThemeMode) => void;
  language: Language;
  setLanguage: (l: Language) => void;
  t: TranslationKeys;
}

// Keep context stable across HMR by attaching to globalThis
const PREFERENCES_CTX_KEY = '__nc_preferences_ctx__';
const PreferencesContext: React.Context<PreferencesContextValue | null> =
  (globalThis as any)[PREFERENCES_CTX_KEY] ??
  ((globalThis as any)[PREFERENCES_CTX_KEY] = createContext<PreferencesContextValue | null>(null));

function getStoredTheme(): ThemeMode {
  try { return (localStorage.getItem('nc-theme') as ThemeMode) || 'dark'; } catch { return 'dark'; }
}
function getStoredLanguage(): Language {
  try { return (localStorage.getItem('nc-lang') as Language) || 'en'; } catch { return 'en'; }
}

function applyTheme(mode: ThemeMode) {
  const root = document.documentElement;
  if (mode === 'system') {
    const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
    root.classList.toggle('dark', prefersDark);
    root.classList.toggle('light', !prefersDark);
  } else {
    root.classList.toggle('dark', mode === 'dark');
    root.classList.toggle('light', mode === 'light');
  }
}

export const PreferencesProvider = ({ children }: { children: ReactNode }) => {
  const [theme, setThemeState] = useState<ThemeMode>(getStoredTheme);
  const [language, setLangState] = useState<Language>(getStoredLanguage);

  const setTheme = useCallback((t: ThemeMode) => {
    setThemeState(t);
    localStorage.setItem('nc-theme', t);
    applyTheme(t);
  }, []);

  const setLanguage = useCallback((l: Language) => {
    setLangState(l);
    void track(AnalyticsEvent.LANGUAGE_CHANGED, { language: l });
    localStorage.setItem('nc-lang', l);
    // Persist the preference on the user's profile so it follows them across
    // devices and sessions.
    void supabase.auth.getSession().then(({ data }) => {
      const uid = data.session?.user?.id;
      if (!uid) return;
      void supabase.from('profiles').update({ language: l } as never).eq('id', uid);
    });
  }, []);

  // On sign-in, adopt the language stored on the profile (source of truth
  // across devices) before the interface settles.
  useEffect(() => {
    const applyFromProfile = async (userId: string) => {
      const { data } = await supabase
        .from('profiles')
        .select('language')
        .eq('id', userId)
        .maybeSingle();
      const remote = (data as { language?: string } | null)?.language;
      if (remote === 'fr' || remote === 'en') {
        setLangState((prev) => {
          if (prev === remote) return prev;
          try { localStorage.setItem('nc-lang', remote); } catch { /* ignore */ }
          return remote;
        });
      } else {
        // No stored preference yet — push the current local choice up.
        const local = getStoredLanguage();
        void supabase.from('profiles').update({ language: local } as never).eq('id', userId);
      }
    };

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      if (session?.user) setTimeout(() => { void applyFromProfile(session.user.id); }, 0);
    });
    void supabase.auth.getSession().then(({ data }) => {
      if (data.session?.user) void applyFromProfile(data.session.user.id);
    });
    return () => subscription.unsubscribe();
  }, []);


  // Apply theme on mount and listen for system changes
  useEffect(() => {
    applyTheme(theme);
    if (theme === 'system') {
      const mq = window.matchMedia('(prefers-color-scheme: dark)');
      const handler = () => applyTheme('system');
      mq.addEventListener('change', handler);
      return () => mq.removeEventListener('change', handler);
    }
  }, [theme]);

  const t = translations[language];

  return (
    <PreferencesContext.Provider value={{ theme, setTheme, language, setLanguage, t }}>
      {children}
    </PreferencesContext.Provider>
  );
};

export const usePreferences = () => {
  const ctx = useContext(PreferencesContext);
  if (!ctx) throw new Error('usePreferences must be used within PreferencesProvider');
  return ctx;
};
